import "./ChatPage.scss";
import type React from "react";
import {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {Box} from "@mui/material";
import {Sidebar} from "./components/Sidebar";
import {useLocation, useNavigate, useParams} from "react-router";
import {useChatContext} from "./context/ChatContext";
import {LoadingBarComponent} from "../../components/loading-bar/LoadingBarComponent.tsx";
import {ChatArea} from "./components/ChatArea/ChatArea";
import {SupersetDashboardPanel} from "./components/SupersetDashboardPanel/SupersetDashboardPanel";
import {useChatAvailableForActiveDatabag} from "./hooks/useChatAvailableForActiveDatabag";
import {useStores} from "../../stores";
import {tryGeneratePath} from "../../routing/routing.utils";

/** Router navigation state accepted by ChatPage (set by the Dashboards gallery). */
export type ChatPageLocationState = {
    /** Open the session's embedded dashboard in fullscreen once it restores. */
    openDashboardFullscreen?: boolean;
    /** When set, only that dashboard triggers fullscreen (guards against a stale panel from a previous session). */
    dashboardUuid?: string | null;
} | null;

export const ChatPage: React.FC = observer(() => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const {bagStore} = useStores();
    const {clearSessionLoading, embeddedDashboard} = useChatContext();
    const {isReady, isAvailable} = useChatAvailableForActiveDatabag();
    const [isDashboardFullscreen, setIsDashboardFullscreen] = useState(false);

    // Leave fullscreen when the dashboard goes away so the chat never stays hidden.
    useEffect(() => {
        if (!embeddedDashboard) {
            setIsDashboardFullscreen(false);
        }
    }, [embeddedDashboard]);

    // Arriving from the Dashboards gallery: go fullscreen once the restored
    // session's dashboard appears, then clear the flag so later dashboard
    // updates (or the user exiting fullscreen) aren't overridden.
    useEffect(() => {
        const state = location.state as ChatPageLocationState;
        if (!embeddedDashboard || !state?.openDashboardFullscreen) return;
        if (state.dashboardUuid && state.dashboardUuid !== embeddedDashboard.uuid) return;
        setIsDashboardFullscreen(true);
        navigate({pathname: location.pathname, search: location.search}, {replace: true});
    }, [embeddedDashboard, location.pathname, location.search, location.state, navigate]);

    useEffect(() => {
        if (!isReady || isAvailable) {
            return;
        }
        const path = tryGeneratePath(
            params,
            ["homepage_dashboard", "home"],
            bagStore.bagId,
        );
        if (path) {
            navigate(path, {replace: true});
        }
    }, [isReady, isAvailable, navigate, params, bagStore.bagId]);

    // ChatProvider outlives this page — clear restore/switch loading on unmount.
    useEffect(() => {
        return () => {
            clearSessionLoading();
        };
    }, [clearSessionLoading]);

    if (isReady && !isAvailable) {
        return <LoadingBarComponent/>;
    }

    // Uses a custom version of the <MainPageFacade/> to better suit the chat page layout
    return (
        <>
            <LoadingBarComponent/>
            <main id="main-component-content">
                <div id="main-page-facade" className="chat-page">
                    <Box
                        className="chat-page-content"
                        sx={{
                            display: "flex",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            overflow: "hidden",
                        }}
                    >
                        {/* display:contents keeps the flex layout; display:none preserves chat state (input draft, scroll) while fullscreen */}
                        <Box sx={{display: embeddedDashboard && isDashboardFullscreen ? "none" : "contents"}}>
                            <Sidebar/>
                            <ChatArea/>
                        </Box>
                        {embeddedDashboard && (
                            <SupersetDashboardPanel
                                // Keyed by uuid + refreshToken: remounts (reloading the iframe) when the
                                // agent switches dashboards OR edits the current one (same embed uuid).
                                key={`${embeddedDashboard.uuid}:${embeddedDashboard.refreshToken}`}
                                dashboard={embeddedDashboard}
                                isFullscreen={isDashboardFullscreen}
                                onToggleFullscreen={() => setIsDashboardFullscreen(prev => !prev)}
                            />
                        )}
                    </Box>
                </div>
            </main>
        </>
    );
});
